import { Link } from "react-router-dom";
import { ArrowRight, Menu, X } from "lucide-react";
import { motion, AnimatePresence, ease as mmEase } from "@/lib/motion";
import Logo from "@/components/Logo";

const ez = mmEase.expo;

const NAV_LINKS = [
  { label: "Features",  id: "features"  },
  { label: "Countries", id: "countries" },
  { label: "Pricing",   id: "pricing"   },
  { label: "Business",  id: "business"  },
];

function smoothScrollTo(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 80;
  window.scrollTo({ top, behavior: "smooth" });
}

export default function Nav({ scrolled, user, mobileNav, setMobileNav, goToApp }) {
  return (
    <>
      <motion.nav
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-void/[0.92] backdrop-blur-2xl border-b border-white/[0.06]" : ""
        }`}
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: ez }}
      >
        <div className="max-w-[1240px] mx-auto px-6 h-[68px] flex items-center gap-6">
          <Logo variant="dark" size="md" />

          <nav className="hidden lg:flex items-center gap-0.5 flex-1 ml-2">
            {NAV_LINKS.map((l) => (
              <button
                key={l.label}
                onClick={() => smoothScrollTo(l.id)}
                className="px-3.5 py-2 rounded-full text-[13px] font-medium transition-all"
                style={{ color: "rgba(255,255,255,0.38)", background: "none", border: "none", cursor: "pointer" }}
                onMouseEnter={(e) => e.currentTarget.style.color = "rgba(255,255,255,0.82)"}
                onMouseLeave={(e) => e.currentTarget.style.color = "rgba(255,255,255,0.38)"}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-1.5">
            <Link
              to={user ? (user.role === "admin" ? "/admin" : "/app/dashboard") : "/login"}
              data-testid="landing-login-link"
              className="text-[13px] font-semibold px-4 py-2.5 rounded-full transition-all"
              style={{ color: "rgba(255,255,255,0.36)" }}
              onMouseEnter={(e) => e.currentTarget.style.color = "rgba(255,255,255,0.78)"}
              onMouseLeave={(e) => e.currentTarget.style.color = "rgba(255,255,255,0.36)"}
            >
              {user ? "Open app" : "Sign in"}
            </Link>
            <motion.button
              data-testid="landing-cta-primary"
              onClick={goToApp}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="btn-primary inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[13px] font-bold"
            >
              {user ? "Dashboard" : "Get started"} <ArrowRight size={13} strokeWidth={2.5} />
            </motion.button>
          </div>

          <button
            onClick={() => setMobileNav(true)}
            className="lg:hidden w-9 h-9 rounded-xl grid place-items-center transition-all"
            style={{ color: "rgba(255,255,255,0.50)" }}
          >
            <Menu size={18} />
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {mobileNav && (
          <>
            <motion.div
              className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileNav(false)}
            />
            <motion.div
              className="fixed right-0 top-0 bottom-0 z-[70] w-72 bg-deep border-l border-white/[0.07] flex flex-col"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ duration: 0.3, ease: ez }}
            >
              <div className="h-[68px] px-5 flex items-center justify-between border-b border-white/[0.06]">
                <Logo variant="dark" size="sm" />
                <button
                  onClick={() => setMobileNav(false)}
                  className="w-8 h-8 rounded-xl grid place-items-center transition-all"
                  style={{ color: "rgba(255,255,255,0.35)" }}
                >
                  <X size={16} />
                </button>
              </div>
              <nav className="flex-1 p-4 space-y-0.5">
                {NAV_LINKS.map((l) => (
                  <button
                    key={l.label}
                    onClick={() => { setMobileNav(false); setTimeout(() => smoothScrollTo(l.id), 320); }}
                    className="flex items-center w-full py-3 px-3.5 rounded-xl text-[14px] font-medium transition-all"
                    style={{ color: "rgba(255,255,255,0.45)", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
                  >
                    {l.label}
                  </button>
                ))}
              </nav>
              <div className="p-5 border-t border-white/[0.06] space-y-2.5">
                <Link
                  to="/login"
                  onClick={() => setMobileNav(false)}
                  className="flex items-center justify-center w-full py-3 rounded-xl border border-white/[0.10] text-[14px] font-semibold transition-all"
                  style={{ color: "rgba(255,255,255,0.55)" }}
                >
                  Sign in
                </Link>
                <button
                  onClick={() => { setMobileNav(false); goToApp(); }}
                  className="btn-primary w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-[14px] font-bold"
                >
                  Get started free <ArrowRight size={15} />
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
